import React from 'react';
import { tie } from './typography';

/**
 * Питання й відповіді.
 *
 * Раніше питання стояли сіткою 2×N карток: відповіді різної довжини, тож
 * сусідні картки закінчувалися на різній висоті й сітка «рвалася», а на
 * сторінці всіх питань це давало двадцять екранів суцільного тексту.
 *
 * variant="accordion" (за замовчуванням) — одна колонка розгортань на
 * <details>/<summary>. Стан відкриття тримає браузер, а не React: розмітка
 * з пререндеру гідрується без жодного useState, розгортання працює ще до
 * того, як завантажився JS, а пошук по сторінці (Ctrl+F) сам відкриває
 * потрібну відповідь. Текст відповідей лишається в розмітці — для
 * пошуковиків і для FAQPage він нікуди не зникає.
 *
 * variant="list" — рівний перелік без розгортань: питання ліворуч,
 * відповідь праворуч, від lg. Для коротких блоків на три-чотири питання,
 * де ховати відповіді немає сенсу.
 *
 * `questionAs="h3"` робить питання заголовками — коли FAQ є розділом зі
 * своїм h2 і питань багато, диктором по них зручніше ходити.
 */
const FaqList = ({ items = [], variant = 'accordion', questionAs, openFirst = false, className = '' }) => {
  const Question = questionAs || 'span';

  if (variant === 'list') {
    // Заголовок усередині <dt> HTML не дозволяє, тож із questionAs перелік
    // складається з div, а без нього — звичайний список визначень.
    const List = questionAs ? 'div' : 'dl';
    const Term = questionAs || 'dt';
    const Desc = questionAs ? 'div' : 'dd';
    return (
      <List className={`divide-y divide-slate-200 border-y border-slate-200 ${className}`}>
        {items.map((item) => (
          <div key={item.q} className="py-5 lg:py-7 grid gap-2 lg:grid-cols-[minmax(0,2fr)_minmax(0,3fr)] lg:gap-12">
            <Term className="text-base lg:text-lg font-semibold font-display text-slate-900 leading-snug text-balance">{tie(item.q)}</Term>
            <Desc className="text-slate-600 leading-relaxed">{tie(item.a)}</Desc>
          </div>
        ))}
      </List>
    );
  }

  return (
    <div className={`space-y-3 ${className}`}>
      {items.map((item, i) => (
        <details
          key={item.q}
          open={openFirst && i === 0 ? true : undefined}
          className="group rounded-2xl border border-slate-200 bg-white open:border-teal-200 transition-colors duration-200"
        >
          <summary className="flex cursor-pointer list-none items-start justify-between gap-4 rounded-2xl px-5 py-4 lg:px-6 lg:py-5 hover:bg-slate-50 group-open:hover:bg-transparent focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-teal-700 [&::-webkit-details-marker]:hidden">
            <Question className="text-base lg:text-lg font-semibold font-display text-slate-900 leading-snug text-balance">
              {tie(item.q)}
            </Question>
            {/* Плюс, що стає мінусом: вертикальна риска стискається до нуля.
                Малюємо двома смужками, а не іконкою — у наборі іконок сайту
                шеврона немає, а тягнути заради нього ще один контур зайве. */}
            <span
              aria-hidden="true"
              className="relative mt-0.5 flex-shrink-0 w-7 h-7 rounded-full border border-slate-200 bg-white text-teal-700 group-open:border-teal-200 group-open:bg-teal-50"
            >
              <span className="absolute left-1/2 top-1/2 w-3 h-0.5 -translate-x-1/2 -translate-y-1/2 rounded-full bg-current"></span>
              <span className="absolute left-1/2 top-1/2 w-0.5 h-3 -translate-x-1/2 -translate-y-1/2 rounded-full bg-current transition-transform duration-200 motion-reduce:transition-none group-open:scale-y-0"></span>
            </span>
          </summary>
          <div className="px-5 pb-5 lg:px-6 lg:pb-6 max-w-3xl text-slate-600 leading-relaxed">{tie(item.a)}</div>
        </details>
      ))}
    </div>
  );
};

export default FaqList;
